import { z } from 'zod'

export const BiopsychosocialEvaluationFormSchema = z
  .object({
    MEDICAL_HISTORY: z
      .object({
        ARTHRITIS: z.boolean().optional(),
        ASTHMA: z.boolean().optional(),
        AUTOIMMUNE_DISORDER: z.boolean().optional(),
        BACK_PROBLEMS: z.boolean().optional(),
        CANCER_CURRENT_OR_PAST: z.boolean().optional(),
        CHRONIC_PAIN: z.boolean().optional(),
        COPD_EMPHYSEMA: z.boolean().optional(),
        DIABETES: z.boolean().optional(),
        HEART_DISEASE: z.boolean().optional(),
        HIGH_BLOOD_PRESSURE: z.boolean().optional(),
        HIGH_CHOLESTEROL: z.boolean().optional(),
        HORMONAL_IMBALANCE: z.boolean().optional(),
        IBS_CROHNS_COLITIS: z.boolean().optional(),
        LIVER_DISEASE: z.boolean().optional(),
        MIGRAINES: z.boolean().optional(),
        SEIZURES: z.boolean().optional(),
        SLEEP_APNEA: z.boolean().optional(),
        STROKE: z.boolean().optional(),
        THYROID_DISORDER: z.boolean().optional(),
        TRAUMATIC_BRAIN_INJURY: z.boolean().optional(),
        ULCERS: z.boolean().optional(),
      })
      .optional(),
    ALLERGIES: z.union([z.literal('reports_no'), z.literal('reports_yes')]),
    ALLERGIES_DETAILS: z.string().min(1).optional(),
    MEDICAL_CONDITIONS: z.union([z.literal('reports_no'), z.literal('reports_yes')]),
    MEDICAL_CONDITIONS_DETAILS: z.string().min(1).optional(),
    ADDITIONAL_NOTES: z.string().min(1).optional(),
    MEDICATIONS: z.union([z.literal('reports_no'), z.literal('reports_yes')]),
    MEDICATIONS_DETAILS: z
      .array(
        z.object({
          NAME: z.string().min(1),
          DOSAGE: z.string().min(1),
          FREQUENCY: z.string().min(1),
        })
      )
      .optional(),
    PRIMARY_CARE_DOCTOR: z
      .object({
        NAME: z.string().min(1),
        PHONE: z.string().min(1),
      })
      .optional(),
    SUBSTANCE_ABUSE: z.object({
      TOBACCO: z.union([z.literal('NONE'), z.literal('USING'), z.literal('HAVE_USED')]),
      ALCOHOL: z.union([z.literal('NONE'), z.literal('USING'), z.literal('HAVE_USED')]),
      MARIJUANA: z.union([z.literal('NONE'), z.literal('USING'), z.literal('HAVE_USED')]),
      NON_PRESCRIBED_DRUGS: z.union([z.literal('NONE'), z.literal('USING'), z.literal('HAVE_USED')]),
      OTHER_RECREATIONAL_DRUGS: z.union([z.literal('NONE'), z.literal('USING'), z.literal('HAVE_USED')]),
      REPORTS_INTEREST_TO_REDUCE_USE: z.boolean(),
      REPORTS_RECEIVING_SUPPORT: z.boolean(),
      SUPPORT_DETAILS: z.string().min(1).optional(),
    }),
    FAMILY_HISTORY: z.union([z.literal('reports_no'), z.literal('reports_yes')]),
    FAMILY_HISTORY_ISSUES: z
      .object({
        TRAUMA_OR_PTSD: z.boolean().optional(),
        SUBSTANCE_ABUSE: z.boolean().optional(),
        ANXIETY: z.boolean().optional(),
        SCHIZOPHRENIA: z.boolean().optional(),
        SUICIDE: z.boolean().optional(),
        DEPRESSION: z.boolean().optional(),
        BIPOLAR_DISORDER: z.boolean().optional(),
        ADHD: z.boolean().optional(),
        INCARCERATION: z.boolean().optional(),
      })
      .optional(),
    FAMILY_HISTORY_DETAILS: z.string().min(1).optional(),
    CURRENT_LIVING_SITUATION: z.string().min(1),
  })
  .superRefine((data, ctx) => {
    if (data.ALLERGIES === 'reports_yes' && !data.ALLERGIES_DETAILS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ALLERGIES_DETAILS'],
        message: 'Allergies details are required if patient reports allergies',
      })
    }

    if (data.MEDICAL_CONDITIONS === 'reports_yes' && !data.MEDICAL_CONDITIONS_DETAILS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['MEDICAL_CONDITIONS_DETAILS'],
        message: 'Medical conditions details are required if patient reports medical conditions',
      })
    }

    if (data.MEDICATIONS === 'reports_yes') {
      if (!data.MEDICATIONS_DETAILS || data.MEDICATIONS_DETAILS.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['MEDICATIONS_DETAILS'],
          message: 'Medications details are required',
        })
      }
    }

    if (data.MEDICATIONS === 'reports_yes' && !data.PRIMARY_CARE_DOCTOR) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['PRIMARY_CARE_DOCTOR', 'NAME'],
        message: 'Primary care doctor name is required if patient reports medications',
      })
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['PRIMARY_CARE_DOCTOR', 'PHONE'],
        message: 'Primary care doctor phone is required if patient reports medications',
      })
    }

    if (
      (data.SUBSTANCE_ABUSE.REPORTS_INTEREST_TO_REDUCE_USE || data.SUBSTANCE_ABUSE.REPORTS_RECEIVING_SUPPORT) &&
      !data.SUBSTANCE_ABUSE.SUPPORT_DETAILS
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['SUBSTANCE_ABUSE', 'SUPPORT_DETAILS'],
        message: 'Support details are required',
      })
    }

    // Family history
    const hasDetails = !!data.FAMILY_HISTORY_DETAILS?.trim()

    if (data.FAMILY_HISTORY === 'reports_yes') {
      const anyFamilyHistoryIssueChecked = !!(
        data.FAMILY_HISTORY_ISSUES?.TRAUMA_OR_PTSD ||
        data.FAMILY_HISTORY_ISSUES?.SUBSTANCE_ABUSE ||
        data.FAMILY_HISTORY_ISSUES?.ANXIETY ||
        data.FAMILY_HISTORY_ISSUES?.SCHIZOPHRENIA ||
        data.FAMILY_HISTORY_ISSUES?.SUICIDE ||
        data.FAMILY_HISTORY_ISSUES?.DEPRESSION ||
        data.FAMILY_HISTORY_ISSUES?.BIPOLAR_DISORDER ||
        data.FAMILY_HISTORY_ISSUES?.ADHD ||
        data.FAMILY_HISTORY_ISSUES?.INCARCERATION
      )
      if (!anyFamilyHistoryIssueChecked && !hasDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['FAMILY_HISTORY_ISSUES'],
          message: 'Please select at least one of the options above.',
        })
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['FAMILY_HISTORY_DETAILS'],
          message: 'Select at least one family history issue or provide details.',
        })
      }
    }
  })
